import { DatePipe } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { fallbackVats, VatInfo } from 'ng-voxloud';
import { FileUploader } from 'ng2-file-upload';
import { VerificationData } from 'src/app/domains/dids/models';
import { VerificationDataService } from 'src/app/domains/dids/services';
import { AddressService } from 'src/app/shared/services/address/address.service';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { environment } from 'src/environments/environment';

const VERIFICATION_DATA_TYPES = [{
  value: 'business',
  label: 'company.dids.verification-data.popup.form.type.business'
}, {
  value: 'private',
  label: 'company.dids.verification-data.popup.form.type.private'
}];
const DEFAULT_VERIFICATION_DATA_TYPE_INDEX = 0;
const MAX_FILE_SIZE = 20 * 1024 * 1024;    // 20Mb
const ALLOWED_FILE_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/jpeg',
  'image/png',
  'text/plain',
  'image/tiff',
  'image/tiff'
];

@Component({
  selector: 'gdp-create-verification-data-popup',
  templateUrl: './create-verification-data-popup.component.html',
  styleUrls: ['./create-verification-data-popup.component.scss']
})
export class CreateVerificationDataPopupComponent implements OnInit {
  public companyId: number;
  public verificationDataList: VerificationData[] = [];
  public loading = {
    getData: false,
    createData: false
  };
  public form: UntypedFormGroup;
  public startDay: NgbDate;
  public minDate = {year: 1900, month:1, day: 1};
  public availableTypes = VERIFICATION_DATA_TYPES;
  private emailPattern = /^\w+([\.-]?\w+)+@\w+([\.:-]?\w+)+(\.[a-zA-Z0-9]{2,})+$/;
  public googleKey = environment.googlePlacesApiKey;
  public vatsInfo: Array<VatInfo> = fallbackVats;
  public defaultCountry: string = "IT";
  public invalidVat: boolean = false;
  @Output() onCreateVerificationData: EventEmitter<any> = new EventEmitter<any>();
  public availableDocTypes: Array<string> = [
    'passport',
    'id',
    'driver_license',
    'company_registration',
    'utility_bill'
  ]
  public selectedDocsType: Array<string> = [];
  public docsUploader: FileUploader;
  public documentsName: Array<string> = [''];
  public documentList: Array<any> = [];

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private verificationDataService: VerificationDataService,
    private addressService: AddressService,
    private notification: NotificationService,
    private ngbCalendar: NgbCalendar,
    private datePipe: DatePipe,
    private cdRef:ChangeDetectorRef,
    private translate: TranslateService
    ) {
      this.docsUploader = new FileUploader({
        url: '',                  // empty because we will upload via our API
        maxFileSize: MAX_FILE_SIZE,
        allowedMimeType: ALLOWED_FILE_TYPES
      });

      this.docsUploader.onAfterAddingFile = this.afterAddingFile();
    }

  ngOnInit() {
    this.getVerificationData();
    this.startDay = this.ngbCalendar.getNext(this.ngbCalendar.getToday());
    this.form = this.fb.group({
      label: [null],
      description: [null],
      type: [VERIFICATION_DATA_TYPES[DEFAULT_VERIFICATION_DATA_TYPE_INDEX]],
      companyName: [null, [Validators.required]],
      vat: [null, [Validators.required]],
      email: [null, [Validators.pattern(this.emailPattern)]],
      phone: [null],
      address: [null, [Validators.required]]
    });
    this.selectedDocsType.push(this.availableDocTypes[0]);
  }

  ngAfterViewChecked() {
    this.phoneValidation();
    this.cdRef.detectChanges();
  }

  phoneValidation() {
    let control = this.form.get('phone');
    if(!control.dirty && control.pristine) {
      control.setErrors(null);
    }
    return (control.invalid && control.dirty) || (control.invalid && control.touched);
  }

  getVerificationData() {
    this.loading.getData = true;
    this.verificationDataService.getVerificationDataByCompanyId(this.companyId).subscribe((res: VerificationData[]) => {
      this.verificationDataList = res;
      this.loading.getData = false;
    }, () => {
      this.verificationDataList = [];
      this.loading.getData = false;
    });
  }

  get getType() {
    return this.form.get('type').value
  }

  isFieldValid(name: string) {
    const control = this.form.get(name);
    return (control.invalid && control.dirty) || (control.invalid && control.touched)
  }

  onCheckVatErrors(event) {
    const control = this.form.get('vat');
    if (!event) {
      control.invalid ? this.invalidVat = true : this.invalidVat = false;
    }
  }

  selectType(type) {
    if(type.value === 'private') {
      this.form.get('companyName').clearValidators();
      this.form.addControl('firstName', new UntypedFormControl(null, [Validators.required]));
      this.form.addControl('lastName', new UntypedFormControl(null, [Validators.required]));
      this.form.addControl('birthPlace', new UntypedFormControl(null));
      this.form.addControl('date', new UntypedFormControl({}));
      this.form.addControl('nationality', new UntypedFormControl(null));
    } else {
      this.form.get('companyName').setValidators([Validators.required])
      this.form.removeControl('firstName');
      this.form.removeControl('lastName');
      this.form.removeControl('birthPlace');
      this.form.removeControl('date');
      this.form.removeControl('nationality');
    }
    this.form.get('type').setValue(type);
  }

  checkVerificationDataList() {
    const value = this.form.value;
    if(this.form.valid) {
      switch(value.type.value) {
        case 'business':
          return this.verificationDataList.some(
            el => (
              el.country_code === this.addressService.getCountry(value.address) &&
              el.company_name === value.companyName)
            );
        case 'private':
          return this.verificationDataList.some(
            el => (
              el.country_code === this.addressService.getCountry(value.address) &&
              el.first_name === value.firstName &&
              el.last_name === value.lastName)
            );
      }
    return;
    }
  }

  selectDocType(index: number, value: string) {
    this.selectedDocsType[index] = value;
  }

  // methods for files uploader
  private afterAddingFile():any {
    return () => {}
  }

  isExtensionContainedInFilename(ev) {
    const ALLOWED_FILE_EXTENSIONS = [
      '.doc',
      '.docx',
      '.pdf',
      '.xls',
      '.xlsx',
      '.jpg',
      '.jpeg',
      '.png',
      '.txt',
      '.tiff',
      '.tif'
    ];
    return ALLOWED_FILE_EXTENSIONS.some(
      el => ev.target.files[0].name.includes(el)
    );
  }

  isFilenameAlreadyTaken(name) {
    return this.documentsName.some(
      el => el === name
    );
  }

  selectedFileOnChanged(ev, index) {
    if(
      this.isExtensionContainedInFilename(ev)
      && !this.isFilenameAlreadyTaken(ev.target.files[0].name)
      ) {
      if(index < this.documentList.length) {
        this.documentList[index] = ev.target.files[0];
        this.documentsName[index] = ev.target.files[0].name;
      } else {
        this.documentList.push(ev.target.files[0]);
        this.documentsName[this.documentsName.length-1] = ev.target.files[0].name;
      }
    }
  }

  public removeFile(index: number):void {
    if(this.documentList.length !== 0) {
      if(this.documentsName[index] === '') {
        this.documentsName.splice(index, 1);
      } else if(this.documentsName.length === 1) {
        this.documentList.splice(index, 1);
        this.documentsName[index] = '';
      } else {
        if (index !== -1) {
          this.documentsName.splice(index, 1);
          this.selectedDocsType.splice(index, 1);
          this.documentList.splice(index, 1);;
        }
      }
    }
  }

  addNewDocument() {
  this.documentsName.push('');
  this.selectedDocsType.push(this.availableDocTypes[0]);
  }

  isAddNewDocDisabled() {
    return this.documentsName[this.documentsName.length-1] === '';
  }

  // method for building object to send docs in form
  buildDocsArray() {
    const docNames = this.documentsName;
    const docTypes = this.selectedDocsType;
    let docs = docNames.map((name, index) => (
      {file_name: name, type: docTypes[index].toUpperCase()}
    ))
    if(docs[docs.length - 1].file_name === '') {
      docs.splice(-1,1)
    }
    return docs;
  }

  createVrificationData() {
    const value = this.form.value;
    const address = this.addressService.getStructuredData(value.address);
    let birthDate;
    if(
      value.type.value === 'private'
      && value.date.year
      && value.date.month
      && value.date.day
      ) {
      birthDate = this.datePipe.transform(
        new Date(value.date.year, value.date.month, value.date.day),
      'yyyy-MM-dd');
    }
    let data = {
      address: {
        zip_code: address.zip,
        city: address.city,
        state: address.state,
        street_name: address.street,
        street_number: address.building,
        building_number: address.building
      },
      company_id: this.companyId,
      company_name: value.companyName,
      country_code: address.state,
      documents: this.buildDocsArray(),
      first_name: value.type.value === 'private' ? value.firstName : null,
      last_name: value.type.value === 'private' ? value.lastName : null,
      birth_date: value.type.value === 'private' ? birthDate : null,
      birth_place: value.type.value === 'private' ? value.birthPlace : null,
      nationality: value.type.value === 'private' ? value.nationality : null,
      tax_number: value.vat.vat,
      type: value.type.value.toUpperCase(),
      phone_number: value.phone === '' ? null : value.phone,
      description: value.description,
      email: value.email,
      label: value.label
    }
    let blobData = new Blob([JSON.stringify(data)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('verification_data', blobData);
    this.documentList.forEach(elem => {
      formData.append('documents', elem);
    });
    this.loading.createData = true
    this.verificationDataService.createVerificationData(formData).subscribe((res: any) => {
      this.onCreateVerificationData.emit(res);
      this.loading.createData = false;
      this.close();
    },
    (err) => {
      if(err.error.status === 400 && err.error.errors && err.error.errors[0].field === 'phoneNumber') {
        this.notification.error('company.dids.verification-data.error-create-phone')
      }
      this.loading.createData = false;
      this.close();
    });
  }

  // methods for translations
  getTranslationAlert() {
    return this.form.get('type').value.value === 'business'
    ? 'company.dids.verification-data.popup.form.alert-business'
    : 'company.dids.verification-data.popup.form.alert-private';
  }

  close() {
    this.activeModal.close();
  }
}
